import React, { useEffect, useMemo, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import axios from "axios";
import { get } from "react-hook-form";
import { useTranslation } from "react-i18next";
import InputErrorMessage from "../InputErrorMessage";
import AttachmentUploader from "../AttachmentUploader/AttachmentUploader";
import { generateRequestConfig } from "../../constants";
import { getMissingAddressFields, isMetadataKey, sanitizeAddressFormData } from "../../pages/PersonalData/personalData.utils";

interface DynamicFormProps {
  data: Record<string, any>;
  onSubmit: (data: Record<string, any>) => Promise<void> | void;
  onDataChange?: (category: string, data: Record<string, any>) => void;
  requestHeaders?: Record<string, string>;
  isEmptyState?: boolean;
  submitState?: "idle" | "draft" | "saving" | "saved" | "error";
  syncVersion?: number;
  fieldMetadata?: Record<string, any>;
}

type LanguageOption = {
  id: string;
  name: string;
  flag?: string;
};

type CountryOption = {
  id: string;
  name: string;
  flag?: string;
  dialCode?: string;
  objectId?: string;
  isoCode?: string;
};

type TimezoneOption = {
  id: string;
  name: string;
};

type SearchableOption = {
  id: string;
  name: string;
  flag?: string;
};

type SearchableSelectProps = {
  id: string;
  name: string;
  value: string;
  options: SearchableOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  registerField: ReturnType<typeof useForm>["register"];
  onChange: (value: string) => void;
  showOptionId?: boolean;
};

type LanguageMultiSelectProps = {
  name: string;
  selectedIds: string[];
  options: LanguageOption[];
  summaryPlaceholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  registerField: ReturnType<typeof useForm>["register"];
};

type PhoneInputProps = {
  id: string;
  name: string;
  value: string;
  countries: CountryOption[];
  phonePlaceholder: string;
  countryPlaceholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  registerField: ReturnType<typeof useForm>["register"];
  onChange: (value: string) => void;
};

const SCHEMA_URL = `${process.env.REACT_APP_API_URL}/personal_data_schema`;
const EMAIL_PATTERN = "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$";

const emptyCategorySchema = { properties: {} };
const fallbackCategorySchemas: Record<string, any> = {
  emails: {
    bsonType: "object",
    properties: {
      primary: {
        bsonType: "object",
        required: ["value"],
        properties: {
          value: {
            bsonType: "string",
            pattern: EMAIL_PATTERN,
          },
        },
        additionalProperties: false,
      },
      work: {
        bsonType: "object",
        properties: {
          value: {
            bsonType: "string",
            pattern: EMAIL_PATTERN,
          },
        },
        additionalProperties: false,
      },
      personal: {
        bsonType: "object",
        properties: {
          value: {
            bsonType: "string",
            pattern: EMAIL_PATTERN,
          },
        },
        additionalProperties: false,
      },
      backup: {
        bsonType: "object",
        properties: {
          value: {
            bsonType: "string",
            pattern: EMAIL_PATTERN,
          },
        },
        additionalProperties: false,
      },
    },
    additionalProperties: false,
  },
  preferences: {
    bsonType: "object",
    properties: {
      language: {
        bsonType: "object",
        description: "Interface language",
        required: ["value"],
        properties: {
          value: {
            bsonType: "string",
            description: "Interface language",
            pattern: "^[a-z]{2}$",
          },
        },
        additionalProperties: false,
      },
      themeMode: {
        bsonType: "object",
        description: "Interface theme mode",
        required: ["value"],
        properties: {
          value: {
            bsonType: "string",
            description: "Interface theme mode",
            enum: ["light", "dark", "auto"],
          },
        },
        additionalProperties: false,
      },
    },
    additionalProperties: false,
  },
};

const mergeCategorySchemaWithFallback = (categoryKey: string, schemaPart: any) => {
  const fallbackSchema = fallbackCategorySchemas[categoryKey];
  if (!fallbackSchema) return schemaPart || emptyCategorySchema;

  return {
    ...fallbackSchema,
    ...(schemaPart || {}),
    properties: {
      ...(fallbackSchema.properties || {}),
      ...(schemaPart?.properties || {}),
    },
  };
};
const fallbackLanguageOptions: LanguageOption[] = [
  { id: "en", name: "English" },
  { id: "ru", name: "Russian" },
  { id: "ro", name: "Romanian" },
  { id: "fr", name: "French" },
];
const SEARCHABLE_INITIAL_LIMIT = 100;
const SEARCHABLE_FILTERED_LIMIT = 200;
const DICTIONARY_PAGE_SIZE = 1000;
// avatar is an attachment object (fileId/fileName/mimeType/url/size/uploadedAt),
// same shape as accountVerification's selfie field below -- without this it falls
// through to the generic object renderer as an "Expand section" with every raw
// attachment field exposed. The image + "Update avatar" UI in PersonalData.tsx
// is the real control; this field is never meant to be edited directly.
const hiddenFormFields = new Set(["accountVerification", "avatar"]);

const getDictionaryItemId = (item: any) => String(item?._id || item?.id || item?.key || "");

const getDictionaryItemName = (item: any, language: string) =>
  item?.value?.[language]?.name || item?.value?.default?.name || item?.name || "";

const mapDictionaryOption = (item: any, language: string): SearchableOption => ({
  id: getDictionaryItemId(item),
  name: getDictionaryItemName(item, language),
  flag: item?.metadata?.flag || "",
});

const fetchDictionaryPageData = async (requestConfig: any) => {
  const firstResponse = await axios(requestConfig);
  const firstData = Array.isArray(firstResponse.data?.data) ? firstResponse.data.data : [];
  const totalPages = Number(firstResponse.data?.total_pages || 1);

  if (totalPages <= 1) return firstData;

  const pageRequests = Array.from({ length: totalPages - 1 }, (_, index) =>
    axios({
      ...requestConfig,
      params: {
        ...(requestConfig.params || {}),
        current_page: index + 2,
      },
    })
  );

  const pageResponses = await Promise.all(pageRequests);
  return pageResponses.reduce((items, response) => {
    const pageData = Array.isArray(response.data?.data) ? response.data.data : [];
    return items.concat(pageData);
  }, firstData);
};

const normalizeDialCode = (value: unknown) => {
  if (typeof value !== "string" && typeof value !== "number") return "";
  const digits = String(value).replace(/\D/g, "");
  return digits ? `+${digits}` : "";
};

const getCountryDialCode = (country: any) => {
  const metadata = country?.metadata || {};
  const fromDictionary = normalizeDialCode(
    metadata.phoneCode ||
    metadata.phone_code ||
    metadata.callingCode ||
    metadata.calling_code ||
    metadata.countryCallingCode ||
    metadata.country_calling_code ||
    metadata.dialCode ||
    metadata.dial_code
  );
  if (fromDictionary) return fromDictionary;

  return "";
};

const compactPhoneValue = (value: string) => {
  const trimmed = String(value || "").trim();
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  if (trimmed.startsWith("+")) return `+${digits}`;
  if (digits.startsWith("00")) return `+${digits.slice(2)}`;
  return digits;
};

const findPhoneCountryByValue = (phoneValue: string, countries: CountryOption[]) => {
  const compact = compactPhoneValue(phoneValue);
  if (!compact.startsWith("+")) return null;

  return countries
    .filter((country) => country.dialCode && compact.startsWith(country.dialCode))
    .sort((left, right) => (right.dialCode?.length || 0) - (left.dialCode?.length || 0))[0] || null;
};

const getPhoneNationalPart = (phoneValue: string, country: CountryOption | null) => {
  const compact = compactPhoneValue(phoneValue);
  if (!compact) return "";
  if (country?.dialCode && compact.startsWith(country.dialCode)) {
    return compact.slice(country.dialCode.length);
  }
  return compact.replace(/^\+/, "");
};

const getDialCodeSortValue = (dialCode = "") => Number(dialCode.replace(/\D/g, "")) || Number.MAX_SAFE_INTEGER;

const sortCountriesByDialCode = (countries: CountryOption[]) =>
  [...countries].sort((left, right) => {
    const codeDiff = getDialCodeSortValue(left.dialCode) - getDialCodeSortValue(right.dialCode);
    if (codeDiff !== 0) return codeDiff;
    return left.name.localeCompare(right.name);
  });

const maskPhone = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 15);
  if (!digits) return "";

  const normalized = digits.startsWith("00") ? digits.slice(2) : digits;
  const segments = [normalized.slice(0, 3), normalized.slice(3, 5), normalized.slice(5, 8), normalized.slice(8, 10), normalized.slice(10, 12), normalized.slice(12, 15)].filter(Boolean);
  return `+${segments.join(" ")}`;
};

const calculatePasswordStrength = (value: string) => {
  if (!value) return 0;

  let score = 0;
  if (value.length >= 8) score += 25;
  if (/[A-Z]/.test(value)) score += 20;
  if (/[a-z]/.test(value)) score += 15;
  if (/\d/.test(value)) score += 20;
  if (/[^A-Za-z0-9]/.test(value)) score += 20;
  return Math.min(score, 100);
};

const humanizeFieldName = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^\w/, (char) => char.toUpperCase());

const normalizeLanguageValue = (value: unknown): string[] => {
  const normalizeLanguageId = (item: unknown) => {
    if (typeof item === "string") return item.trim().toLowerCase();
    if (item && typeof item === "object") {
      const language = item as Record<string, unknown>;
      const candidate = language.id || language.iso6391 || language.iso_639_1 || language.value;
      return typeof candidate === "string" ? candidate.trim().toLowerCase() : "";
    }
    return "";
  };

  if (Array.isArray(value)) return value.map(normalizeLanguageId).filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    return value.split(",").map(normalizeLanguageId).filter(Boolean);
  }
  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => normalizeLanguageId(item) || key.trim().toLowerCase())
      .filter(Boolean);
  }
  return [];
};

const formatSelectedLanguages = (selectedIds: string[], options: LanguageOption[]) => {
  if (selectedIds.length === 0) return "";

  const selectedNames = selectedIds
    .map((id) => options.find((option) => option.id === id)?.name || id.toUpperCase())
    .filter(Boolean);

  if (selectedNames.length <= 3) return selectedNames.join(", ");
  return `${selectedNames.slice(0, 3).join(", ")} +${selectedNames.length - 3}`;
};

const isEmptyValue = (value: unknown) => value === "" || value === null || value === undefined;

const hasFilledValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.some(hasFilledValue);
  if (typeof value !== "object") return true;

  return Object.entries(value as Record<string, unknown>)
    .filter(([key]) => !isMetadataKey(key))
    .some(([, item]) => hasFilledValue(item));
};

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  id,
  name,
  value,
  options,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  registerField,
  onChange,
  showOptionId = true,
}) => {
  const selectedOption = useMemo(
    () => options.find((option) => option.id.toLowerCase() === String(value || "").toLowerCase()),
    [options, value]
  );
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (selectedOption) {
      setQuery(`${selectedOption.flag ? `${selectedOption.flag} ` : ""}${selectedOption.name}`);
      return;
    }

    setQuery("");
  }, [selectedOption]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = useMemo(() => {
    if (!normalizedQuery || (selectedOption && query.includes(selectedOption.name))) {
      return options.slice(0, SEARCHABLE_INITIAL_LIMIT);
    }

    return options
      .filter((option) => {
        const label = `${option.name} ${option.id}`.toLowerCase();
        return label.includes(normalizedQuery);
      })
      .slice(0, SEARCHABLE_FILTERED_LIMIT);
  }, [normalizedQuery, options, query, selectedOption]);

  return (
    <div className="pd-searchable-select">
      <input type="hidden" {...registerField(name)} />
      <input
        id={id}
        className="pd-control pd-searchable-select__input"
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={filteredOptions.length > 0}
        aria-controls={`${id}_options`}
        onChange={(event) => {
          setQuery(event.target.value);
          if (!event.target.value.trim()) {
            onChange("");
          }
        }}
        onBlur={() => {
          window.setTimeout(() => {
            if (selectedOption) {
              setQuery(`${selectedOption.flag ? `${selectedOption.flag} ` : ""}${selectedOption.name}`);
              return;
            }

            setQuery("");
          }, 120);
        }}
      />
      <div className="pd-searchable-select__hint">{searchPlaceholder}</div>
      <div id={`${id}_options`} className="pd-searchable-select__panel" role="listbox">
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <button
              type="button"
              className="pd-searchable-select__option"
              key={option.id}
              role="option"
              aria-selected={option.id === value}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(option.id);
                setQuery(`${option.flag ? `${option.flag} ` : ""}${option.name}`);
              }}
            >
              <span>{option.flag ? `${option.flag} ` : ""}{option.name}</span>
              {showOptionId && <small>{option.id}</small>}
            </button>
          ))
        ) : (
          <div className="pd-searchable-select__empty">{emptyLabel}</div>
        )}
      </div>
    </div>
  );
};

const LanguageMultiSelect: React.FC<LanguageMultiSelectProps> = ({
  name,
  selectedIds,
  options,
  summaryPlaceholder,
  searchPlaceholder,
  emptyLabel,
  registerField,
}) => {
  const [query, setQuery] = useState("");
  const selectedLabel = formatSelectedLanguages(selectedIds, options);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = useMemo(() => {
    if (!normalizedQuery) return options;

    return options.filter((option) => {
      const label = `${option.name} ${option.id}`.toLowerCase();
      return label.includes(normalizedQuery);
    });
  }, [normalizedQuery, options]);

  return (
    <details className="pd-language-select">
      <summary className="pd-language-select__summary">
        <span className={selectedLabel ? "pd-language-select__value" : "pd-language-select__placeholder"}>
          {selectedLabel || summaryPlaceholder}
        </span>
        {selectedIds.length > 0 && (
          <span className="pd-language-select__count">
            {selectedIds.length}
          </span>
        )}
      </summary>

      <div className="pd-language-select__panel">
        <input
          type="search"
          className="pd-control pd-language-select__search"
          value={query}
          placeholder={searchPlaceholder}
          autoComplete="off"
          onChange={(event) => setQuery(event.target.value)}
        />

        {filteredOptions.length > 0 ? (
          filteredOptions.map((language) => (
            <label className="pd-language-option" key={language.id}>
              <input
                type="checkbox"
                value={language.id}
                defaultChecked={selectedIds.includes(language.id)}
                {...registerField(name)}
              />
              <span className="pd-language-option__text">
                {language.flag && <span aria-hidden="true">{language.flag}</span>}
                <span>{language.name}</span>
              </span>
            </label>
          ))
        ) : (
          <div className="pd-searchable-select__empty">{emptyLabel}</div>
        )}
      </div>
    </details>
  );
};


const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  name,
  value,
  countries,
  phonePlaceholder,
  countryPlaceholder,
  searchPlaceholder,
  emptyLabel,
  registerField,
  onChange,
}) => {
  const [query, setQuery] = useState("");
  const [countryOpen, setCountryOpen] = useState(false);
  const selectedCountry = useMemo(() => findPhoneCountryByValue(value, countries), [countries, value]);
  const nationalPart = getPhoneNationalPart(value, selectedCountry);
  const normalizedQuery = query.trim().toLowerCase();
  const countryLabel = selectedCountry
    ? `${selectedCountry.flag ? `${selectedCountry.flag} ` : ""}${selectedCountry.dialCode}`
    : countryPlaceholder;

  const filteredCountries = useMemo(() => {
    const withDialCodes = sortCountriesByDialCode(countries.filter((country) => country.dialCode));
    if (!normalizedQuery) return withDialCodes;

    return withDialCodes.filter((country) => {
      const label = `${country.name} ${country.id} ${country.dialCode}`.toLowerCase();
      return label.includes(normalizedQuery);
    });
  }, [countries, normalizedQuery]);

  const selectCountry = (country: CountryOption) => {
    const digits = nationalPart.replace(/\D/g, "");
    onChange(digits ? `${country.dialCode}${digits}` : country.dialCode || "");
    setCountryOpen(false);
    setQuery("");
  };

  return (
    <div className="pd-phone-input">
      <input type="hidden" {...registerField(name)} />
      <div className="pd-phone-input__country">
        <button
          type="button"
          className="pd-phone-input__country-button"
          aria-haspopup="listbox"
          aria-expanded={countryOpen}
          onClick={() => setCountryOpen((current) => !current)}
        >
          <span>{countryLabel}</span>
        </button>
        {countryOpen && (
          <div className="pd-phone-input__panel" role="listbox">
            <input
              className="pd-control pd-phone-input__search"
              type="search"
              value={query}
              placeholder={searchPlaceholder}
              autoComplete="off"
              onChange={(event) => setQuery(event.target.value)}
            />
            <div className="pd-phone-input__options">
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <button
                    type="button"
                    className="pd-phone-input__option"
                    key={`${country.id}-${country.dialCode}`}
                    role="option"
                    aria-selected={country.id === selectedCountry?.id}
                    onClick={() => selectCountry(country)}
                  >
                    <strong>{country.dialCode}</strong>
                    <span>({country.flag ? `${country.flag} ` : ""}{country.name})</span>
                  </button>
                ))
              ) : (
                <div className="pd-searchable-select__empty">{emptyLabel}</div>
              )}
            </div>
          </div>
        )}
      </div>
      <input
        id={id}
        className="pd-control pd-phone-input__number"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={nationalPart}
        placeholder={phonePlaceholder}
        onChange={(event) => {
          const raw = event.target.value;
          const compact = compactPhoneValue(raw);
          if (!compact) {
            onChange("");
            return;
          }

          if (compact.startsWith("+")) {
            onChange(compact);
            return;
          }

          const dialCode = selectedCountry?.dialCode || "";
          onChange(dialCode ? `${dialCode}${compact.replace(/\D/g, "")}` : compact);
        }}
      />
    </div>
  );
};

const DynamicFormWithNestedTabs: React.FC<DynamicFormProps> = ({
  data,
  onSubmit,
  onDataChange,
  requestHeaders = {},
  isEmptyState = false,
  submitState = "idle",
  syncVersion = 0,
  fieldMetadata = {},
}) => {
  const { t, i18n } = useTranslation();
  const categoryKey = Object.keys(data)[0];
  const categoryData = data[categoryKey] ?? {};
  const dictionaryLanguage = (i18n.language || "en").split("-")[0].toUpperCase();

  const [zodSchema, setZodSchema] = useState<z.ZodType<any, any> | null>(null);
  const [categorySchema, setCategorySchema] = useState<any | null>(null);
  const [localSubmitState, setLocalSubmitState] = useState<"idle" | "saving" | "success">("idle");
  const [validationFeedback, setValidationFeedback] = useState("");
  const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>(fallbackLanguageOptions);
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);
  const [regionOptionsByAddress, setRegionOptionsByAddress] = useState<Record<string, SearchableOption[]>>({});
  const [cityOptionsByAddress, setCityOptionsByAddress] = useState<Record<string, SearchableOption[]>>({});
  const [timezoneOptions, setTimezoneOptions] = useState<TimezoneOption[]>([]);
  const [selectedTechnicalPassportCategory, setSelectedTechnicalPassportCategory] = useState("");
  const [selectedTaxCountry, setSelectedTaxCountry] = useState("");
  const [taxIdentifierValue, setTaxIdentifierValue] = useState("");
  const [documentsViewMode, setDocumentsViewMode] = useState<"table" | "grid">("table");
  const [isAddingDocument, setIsAddingDocument] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [selectedDocumentCountry, setSelectedDocumentCountry] = useState("");

  const generateZodSchema = (schemaPart: any): z.ZodType<any, any> => {
    if (!schemaPart?.properties) return z.record(z.string(), z.any());

    const shape: Record<string, z.ZodType<any, any>> = {};

    Object.entries(schemaPart.properties).forEach(([key, field]: [string, any]) => {
      if (hiddenFormFields.has(key)) return;

      let zodField: z.ZodType<any, any>;

      if (key === "languages") {
        zodField = z.any().refine(
          (value) =>
            value === "" ||
            value === null ||
            value === undefined ||
            Array.isArray(value) ||
            (typeof value === "object" && !Array.isArray(value)),
          { message: t("personalData.validation.invalidValue") }
        );
      } else if (field.properties?.value) {
        const valueSchema = field.properties.value;

        const isRequired = field.required?.includes("value");
        const hasPattern = Boolean(valueSchema.pattern);

        if (valueSchema.enum) {
          zodField = z.string().refine((value) => valueSchema.enum.includes(value), {
            message: t("personalData.validation.oneOf", { values: valueSchema.enum.join(", ") }),
          });
        } else if (valueSchema.pattern) {
          const pattern = new RegExp(valueSchema.pattern);
          zodField = z.any().refine(
            (value) => isEmptyValue(value) || (typeof value === "string" && pattern.test(value)),
            { message: t("personalData.validation.invalidFormat") }
          );
        } else if (valueSchema.bsonType === "date") {
          zodField = z.string().optional().nullable();
        } else if (valueSchema.minLength !== undefined || valueSchema.maxLength !== undefined) {
          zodField = z
            .string()
            .min(valueSchema.minLength || 0, t("personalData.validation.minLength", { count: valueSchema.minLength || 0 }))
            .max(valueSchema.maxLength || 9999, t("personalData.validation.maxLength", { count: valueSchema.maxLength || 9999 }));
        } else {
          zodField = z.string();
        }

        if (isRequired) {
          const baseZodField = zodField;
          zodField = z.any().superRefine((value, ctx) => {
            const hasValue = !isEmptyValue(value) && String(value).trim() !== "";

            if (!hasValue) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("personalData.validation.required"),
              });
              return;
            }

            const parsed = baseZodField.safeParse(value);
            if (!parsed.success) {
              const issue = parsed.error.issues[0];
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: issue?.message || t("personalData.validation.invalidValue"),
              });
            }
          });
        } else if (!hasPattern) {
          const baseZodField = zodField;
          zodField = z.any().refine(
            (value) => isEmptyValue(value) || baseZodField.safeParse(value).success,
            { message: t("personalData.validation.invalidValue") }
          );
        }
      } else if (field.properties) {
        zodField = generateZodSchema(field);
      } else if (field.patternProperties) {
        const inner = Object.values(field.patternProperties)[0] as any;
        zodField = z.record(z.string(), generateZodSchema(inner) as z.ZodTypeAny);
      } else {
        zodField = z.any();
      }

      shape[key] = zodField;
    });

    return z.object(shape);
  };

  useEffect(() => {
    axios
      .get(SCHEMA_URL, {
        headers: requestHeaders,
        withCredentials: true,
      })
      .then((res) => {
        const fullSchema = res.data;
        const dataSchema = fullSchema?.properties?.data?.properties ?? {};
        const selectedCategorySchema = mergeCategorySchemaWithFallback(categoryKey, dataSchema[categoryKey]);
        setCategorySchema(selectedCategorySchema);
        setZodSchema(z.object({ [categoryKey]: generateZodSchema(selectedCategorySchema) }));
      })
      .catch((error) => {
        console.error(t("personalData.errors.schemaLoad"), error);
        setCategorySchema(emptyCategorySchema);
        setZodSchema(z.object({ [categoryKey]: z.any() }));
      });
  }, [categoryKey, requestHeaders, t]);

  useEffect(() => {
    if (categoryKey !== "profile" && categoryKey !== "preferences" && categoryKey !== "phones" && categoryKey !== "addresses" && categoryKey !== "documents") return;

    let isMounted = true;

    const fetchLanguages = async () => {
      try {
        const requestConfigLanguages = generateRequestConfig(
          "get",
          "languages",
          {},
          {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          {
            items_per_page: DICTIONARY_PAGE_SIZE,
            current_page: 1,
          }
        );

        const languages = await fetchDictionaryPageData(requestConfigLanguages);
        const nextLanguageOptions = languages
          .map((lang: any) => ({
            id: lang.metadata?.iso_639_1?.toLowerCase() || "",
            name: lang.value?.default?.name || "",
            flag: lang.metadata?.flag || "",
          }))
          .filter((lang: LanguageOption) => lang.id && lang.name);

        if (isMounted && nextLanguageOptions.length > 0) {
          setLanguageOptions(nextLanguageOptions);
        }
      } catch (error) {
        console.error(t("personalData.errors.languagesLoad", { defaultValue: "Error loading languages" }), error);
      }
    };

    if (categoryKey === "profile" || categoryKey === "preferences") {
      fetchLanguages();
    }

    const fetchCountries = async () => {
      try {
        const requestConfigCountries = generateRequestConfig(
          "get",
          "countries",
          {},
          {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          {
            items_per_page: DICTIONARY_PAGE_SIZE,
            current_page: 1,
            fields: `key,value.${dictionaryLanguage}.name,value.default.name,metadata.flag,metadata.iso31662,metadata.phoneCode,metadata.phone_code,metadata.callingCode,metadata.calling_code,metadata.countryCallingCode,metadata.country_calling_code,metadata.dialCode,metadata.dial_code`,
          }
        );

        const countries = await fetchDictionaryPageData(requestConfigCountries);

        const nextCountryOptions = countries
          .map((country: any) => {
            const objectId = getDictionaryItemId(country);
            const isoCode = country.metadata?.iso31662 || country.key || "";

            return {
              id: isoCode || objectId,
              objectId,
              isoCode,
              name: getDictionaryItemName(country, dictionaryLanguage),
              flag: country.metadata?.flag || "",
              dialCode: getCountryDialCode(country),
            };
          })
          .filter((country: CountryOption) => country.id && country.name)
          .sort((left: CountryOption, right: CountryOption) => left.name.localeCompare(right.name));

        if (isMounted && nextCountryOptions.length > 0) {
          setCountryOptions(nextCountryOptions);
        }
      } catch (error) {
        console.error(t("personalData.errors.countriesLoad", { defaultValue: "Error loading countries" }), error);
      }
    };

    if (categoryKey === "profile" || categoryKey === "phones" || categoryKey === "addresses" || categoryKey === "documents") {
      fetchCountries();
    }

    const fetchTimezones = async () => {
      try {
        const requestConfigTimezones = generateRequestConfig(
          "get",
          "timezones",
          {},
          {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          {
            items_per_page: DICTIONARY_PAGE_SIZE,
            current_page: 1,
          }
        );

        const timezones = await fetchDictionaryPageData(requestConfigTimezones);

        const nextTimezoneOptions = timezones
          .map((timezone: any) => {
            const name = timezone.value?.default?.name || "";

            return {
              id: name,
              name,
            };
          })
          .filter((timezone: TimezoneOption) => timezone.id && timezone.name)
          .sort((left: TimezoneOption, right: TimezoneOption) => left.name.localeCompare(right.name));

        if (isMounted && nextTimezoneOptions.length > 0) {
          setTimezoneOptions(nextTimezoneOptions);
        }
      } catch (error) {
        console.error(t("personalData.errors.timezonesLoad", { defaultValue: "Error loading timezones" }), error);
      }
    };

    if (categoryKey === "profile") {
      fetchTimezones();
    }

    return () => {
      isMounted = false;
    };
  }, [categoryKey, dictionaryLanguage, t]);

  const resolvedZodSchema = zodSchema ?? z.object({ [categoryKey]: z.any() });
  const resolvedCategorySchema = categorySchema ?? emptyCategorySchema;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: categoryKey === "addresses" ? undefined : zodResolver(resolvedZodSchema) as any,
    defaultValues: { [categoryKey]: categoryData },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const liveValues = watch();

  useEffect(() => {
    reset({ [categoryKey]: categoryData });
  }, [categoryKey, reset, syncVersion]);

  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (categoryKey === "addresses" && name?.startsWith("addresses.") && name.endsWith(".country")) {
        const addressKey = name.split(".")[1];
        setValue(`addresses.${addressKey}.region`, "", { shouldDirty: true, shouldValidate: true });
        setValue(`addresses.${addressKey}.city`, "", { shouldDirty: true, shouldValidate: true });
      }

      if (categoryKey === "addresses" && name?.startsWith("addresses.") && name.endsWith(".region")) {
        const addressKey = name.split(".")[1];
        setValue(`addresses.${addressKey}.city`, "", { shouldDirty: true, shouldValidate: true });
      }

      onDataChange?.(categoryKey, values?.[categoryKey] ?? {});
    });

    return () => subscription.unsubscribe();
  }, [categoryKey, onDataChange, setValue, watch]);

  const addressValues = categoryKey === "addresses" && liveValues?.addresses && typeof liveValues.addresses === "object"
    ? liveValues.addresses
    : {};
  const addressCountrySignature = Object.entries(addressValues)
    .map(([addressKey, value]: [string, any]) => `${addressKey}:${value?.country || ""}`)
    .sort()
    .join("|");
  const addressRegionSignature = Object.entries(addressValues)
    .map(([addressKey, value]: [string, any]) => `${addressKey}:${value?.region || ""}`)
    .sort()
    .join("|");
  const addressCountryOptions = useMemo(
    () => countryOptions
      .map((country) => ({
        id: country.objectId || country.id,
        name: country.name,
        flag: country.flag,
      }))
      .filter((country) => country.id && country.name),
    [countryOptions]
  );

  useEffect(() => {
    if (categoryKey !== "addresses") return;

    let isMounted = true;

    const fetchRegions = async () => {
      const entries = Object.entries(addressValues) as Array<[string, any]>;
      const nextOptionsByAddress: Record<string, SearchableOption[]> = {};

      await Promise.all(entries.map(async ([addressKey, address]) => {
        const countryValue = String(address?.country || "");
        if (!countryValue) {
          nextOptionsByAddress[addressKey] = [];
          return;
        }

        const countryParentId = countryOptions.find((country) =>
          country.objectId === countryValue || country.id === countryValue || country.isoCode === countryValue
        )?.objectId || countryValue;

        try {
          const requestConfigRegions = generateRequestConfig(
            "get",
            "regions",
            {},
            {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            {
              items_per_page: DICTIONARY_PAGE_SIZE,
              current_page: 1,
              parentId: countryParentId,
              fields: `key,value.${dictionaryLanguage}.name,value.default.name,metadata.coordinates`,
            }
          );

          const regions = await fetchDictionaryPageData(requestConfigRegions);
          nextOptionsByAddress[addressKey] = regions
            .map((region: any) => mapDictionaryOption(region, dictionaryLanguage))
            .filter((region: SearchableOption) => region.id && region.name)
            .sort((left: SearchableOption, right: SearchableOption) => left.name.localeCompare(right.name));
        } catch (error) {
          console.error(t("personalData.errors.regionsLoad", { defaultValue: "Error loading regions" }), error);
          nextOptionsByAddress[addressKey] = [];
        }
      }));

      if (isMounted) setRegionOptionsByAddress(nextOptionsByAddress);
    };

    fetchRegions();

    return () => {
      isMounted = false;
    };
  }, [addressCountrySignature, categoryKey, countryOptions, dictionaryLanguage, t]);

  useEffect(() => {
    if (categoryKey !== "addresses") return;

    let isMounted = true;

    const fetchCities = async () => {
      const entries = Object.entries(addressValues) as Array<[string, any]>;
      const nextOptionsByAddress: Record<string, SearchableOption[]> = {};

      await Promise.all(entries.map(async ([addressKey, address]) => {
        const regionValue = String(address?.region || "");
        if (!regionValue) {
          nextOptionsByAddress[addressKey] = [];
          return;
        }

        try {
          const requestConfigCities = generateRequestConfig(
            "get",
            "cities",
            {},
            {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            {
              items_per_page: DICTIONARY_PAGE_SIZE,
              current_page: 1,
              parentId: regionValue,
              fields: `key,value.${dictionaryLanguage}.name,value.default.name,metadata.coordinates`,
            }
          );

          const cities = await fetchDictionaryPageData(requestConfigCities);
          nextOptionsByAddress[addressKey] = cities
            .map((city: any) => mapDictionaryOption(city, dictionaryLanguage))
            .filter((city: SearchableOption) => city.id && city.name)
            .sort((left: SearchableOption, right: SearchableOption) => left.name.localeCompare(right.name));
        } catch (error) {
          console.error(t("personalData.errors.citiesLoad", { defaultValue: "Error loading cities" }), error);
          nextOptionsByAddress[addressKey] = [];
        }
      }));

      if (isMounted) setCityOptionsByAddress(nextOptionsByAddress);
    };

    fetchCities();

    return () => {
      isMounted = false;
    };
  }, [addressRegionSignature, categoryKey, dictionaryLanguage, t]);

  useEffect(() => {
    if (localSubmitState !== "success") return;
    const timeoutId = window.setTimeout(() => setLocalSubmitState("idle"), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [localSubmitState]);

  const renderFieldControl = (fullPath: string, fieldName: string) => {
    const currentValue = get(liveValues, fullPath) ?? "";
    const inputId = fullPath.replace(/\./g, "_");
    const isDateField = fullPath.includes("birthDate") || fullPath.includes("issuedDate") || fullPath.includes("expiryDate");
    const isPhoneField = fullPath.startsWith("phones.") || fullPath.includes(".phones.");
    const isEmailField = fullPath.includes(".emails.");
    const addressPathMatch = fullPath.match(/^addresses\.([^.]+)\./);
    const addressKey = addressPathMatch?.[1] || "";
    const isDocumentCountryField = fullPath.startsWith("documents.") && ["country", "issuingCountry", "citizenshipCountry"].includes(fieldName);
    const isAddressCountryField = Boolean(addressKey) && fieldName === "country";
    const isAddressRegionField = Boolean(addressKey) && fieldName === "region";
    const isAddressCityField = Boolean(addressKey) && fieldName === "city";
    const isPasswordField = fieldName.toLowerCase().includes("password");
    const isTextArea = fieldName === "aboutMe";
    const fieldLabel = t(`personalData.fields.${fieldName}`, { defaultValue: humanizeFieldName(fieldName) });
    const registerOptions =
      isPhoneField
        ? {
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
              event.target.value = maskPhone(event.target.value);
            },
          }
        : undefined;

    if (fieldName === "gender") {
      return (
        <select
          id={inputId}
          className="pd-control pd-select"
          {...register(fullPath)}
          defaultValue={currentValue}
        >
          <option value="">{t("personalData.placeholders.selectGender")}</option>
          <option value="male">{t("personalData.values.gender.male")}</option>
          <option value="female">{t("personalData.values.gender.female")}</option>
          <option value="other">{t("personalData.values.gender.other")}</option>
          <option value="prefer_not_to_say">{t("personalData.values.gender.prefer_not_to_say")}</option>
        </select>
      );
    }

    if (fieldName === "placeOfResidence") {
      return (
        <SearchableSelect
          id={inputId}
          name={fullPath}
          value={String(currentValue || "").toUpperCase()}
          options={countryOptions}
          placeholder={t("personalData.placeholders.selectCountry")}
          searchPlaceholder={t("personalData.placeholders.searchCountry")}
          emptyLabel={t("personalData.ui.noOptions")}
          registerField={register}
          onChange={(value) => setValue(fullPath, value, { shouldDirty: true, shouldValidate: true })}
        />
      );
    }

    if (isDocumentCountryField) {
      return (
        <SearchableSelect
          id={inputId}
          name={fullPath}
          value={String(currentValue || "")}
          options={addressCountryOptions}
          placeholder={t("personalData.placeholders.selectCountry")}
          searchPlaceholder={t("personalData.placeholders.searchCountry")}
          emptyLabel={t("personalData.ui.noOptions")}
          registerField={register}
          onChange={(value) => setValue(fullPath, value, { shouldDirty: true, shouldValidate: true })}
          showOptionId={false}
        />
      );
    }

    if (isAddressCountryField) {
      return (
        <SearchableSelect
          id={inputId}
          name={fullPath}
          value={String(currentValue || "")}
          options={addressCountryOptions}
          placeholder={t("personalData.placeholders.selectCountry")}
          searchPlaceholder={t("personalData.placeholders.searchCountry")}
          emptyLabel={t("personalData.ui.noOptions")}
          registerField={register}
          showOptionId={false}
          onChange={(value) => {
            setValue(fullPath, value, { shouldDirty: true, shouldValidate: true });
            setValue(`addresses.${addressKey}.region`, "", { shouldDirty: true, shouldValidate: true });
            setValue(`addresses.${addressKey}.city`, "", { shouldDirty: true, shouldValidate: true });
          }}
        />
      );
    }

    if (isAddressRegionField) {
      return (
        <SearchableSelect
          id={inputId}
          name={fullPath}
          value={String(currentValue || "")}
          options={regionOptionsByAddress[addressKey] || []}
          placeholder={t("personalData.placeholders.selectRegion", { defaultValue: "Select region" })}
          searchPlaceholder={t("personalData.placeholders.searchRegion", { defaultValue: "Search region" })}
          emptyLabel={t("personalData.ui.noOptions")}
          registerField={register}
          showOptionId={false}
          onChange={(value) => {
            setValue(fullPath, value, { shouldDirty: true, shouldValidate: true });
            setValue(`addresses.${addressKey}.city`, "", { shouldDirty: true, shouldValidate: true });
          }}
        />
      );
    }

    if (isAddressCityField) {
      return (
        <SearchableSelect
          id={inputId}
          name={fullPath}
          value={String(currentValue || "")}
          options={cityOptionsByAddress[addressKey] || []}
          placeholder={t("personalData.placeholders.selectCity", { defaultValue: "Select city" })}
          searchPlaceholder={t("personalData.placeholders.searchCity", { defaultValue: "Search city" })}
          emptyLabel={t("personalData.ui.noOptions")}
          registerField={register}
          onChange={(value) => setValue(fullPath, value, { shouldDirty: true, shouldValidate: true })}
          showOptionId={false}
        />
      );
    }

    if (fieldName === "timezone") {
      return (
        <SearchableSelect
          id={inputId}
          name={fullPath}
          value={currentValue}
          options={timezoneOptions}
          placeholder={t("personalData.placeholders.selectTimezone")}
          searchPlaceholder={t("personalData.placeholders.searchTimezone")}
          emptyLabel={t("personalData.ui.noOptions")}
          registerField={register}
          onChange={(value) => setValue(fullPath, value, { shouldDirty: true, shouldValidate: true })}
        />
      );
    }

    if (fullPath === "preferences.language") {
      return (
        <SearchableSelect
          id={inputId}
          name={fullPath}
          value={String(currentValue || "").toLowerCase()}
          options={languageOptions}
          placeholder={t("personalData.placeholders.selectLanguage")}
          searchPlaceholder={t("personalData.placeholders.searchLanguages")}
          emptyLabel={t("personalData.ui.noOptions")}
          registerField={register}
          onChange={(value) => setValue(fullPath, value, { shouldDirty: true, shouldValidate: true })}
        />
      );
    }

    if (fullPath === "preferences.themeMode") {
      return (
        <select
          id={inputId}
          className="pd-control pd-select"
          {...register(fullPath)}
          defaultValue={currentValue || "auto"}
        >
          <option value="auto">{t("personalData.values.theme.auto", { defaultValue: "By time of day" })}</option>
          <option value="light">{t("personalData.values.theme.light", { defaultValue: "Light" })}</option>
          <option value="dark">{t("personalData.values.theme.dark", { defaultValue: "Dark" })}</option>
        </select>
      );
    }

    if (fieldName === "languages") {
      const selectedLanguages = normalizeLanguageValue(currentValue);

      return (
        <LanguageMultiSelect
          name={fullPath}
          selectedIds={selectedLanguages}
          options={languageOptions}
          summaryPlaceholder={t("personalData.placeholders.selectLanguages")}
          searchPlaceholder={t("personalData.placeholders.searchLanguages")}
          emptyLabel={t("personalData.ui.noOptions")}
          registerField={register}
        />
      );
    }

    if (isPhoneField) {
      return (
        <PhoneInput
          id={inputId}
          name={fullPath}
          value={String(currentValue || "")}
          countries={countryOptions}
          phonePlaceholder={t("personalData.placeholders.enterPhone", { defaultValue: "Phone number" })}
          countryPlaceholder={t("personalData.placeholders.selectPhoneCountry", { defaultValue: "Code" })}
          searchPlaceholder={t("personalData.placeholders.searchCountry")}
          emptyLabel={t("personalData.ui.noOptions")}
          registerField={register}
          onChange={(nextValue) => setValue(fullPath, nextValue, { shouldDirty: true, shouldValidate: true })}
        />
      );
    }

    if (isTextArea) {
      return (
        <textarea
          id={inputId}
          className="pd-control pd-textarea"
          rows={5}
          placeholder={t("personalData.placeholders.aboutMe")}
          {...register(fullPath)}
        />
      );
    }

    return (
      <div className={`pd-input-shell ${isPasswordField ? "has-password-meter" : ""}`}>
        <input
          id={inputId}
          className="pd-control"
          type={isPasswordField ? "password" : isDateField ? "date" : isPhoneField ? "tel" : "text"}
          inputMode={isEmailField ? "email" : isPhoneField ? "tel" : undefined}
          autoComplete={isEmailField ? "email" : undefined}
          placeholder={t("personalData.placeholders.enterField", { field: fieldLabel.toLowerCase() })}
          {...register(fullPath, registerOptions)}
        />

        {isPasswordField && (
          <div className="pd-password-meter">
            <span
              className="pd-password-meter__bar"
              style={{ width: `${calculatePasswordStrength(currentValue)}%` }}
            />
          </div>
        )}
      </div>
    );
  };

  const isVerificationTracked = (fullPath: string) => {
    if (fullPath === "profile.placeOfResidence") return true;
    if (fullPath.startsWith("emails.")) return true;
    if (fullPath.startsWith("phones.")) return true;

    const verifiableDocumentPaths = new Set([
      "documents.passport",
      "documents.driverLicense",
      "documents.idCard",
      "documents.birthCertificate",
    ]);

    const isRepeatableDocumentItem =
      /^documents\.visa\.[^.]+$/.test(fullPath) ||
      /^documents\.residencePermit\.[^.]+$/.test(fullPath) ||
      /^documents\.technicalPassports\.[^.]+$/.test(fullPath);

    return verifiableDocumentPaths.has(fullPath) || isRepeatableDocumentItem || Boolean(get(fieldMetadata, `${fullPath}.__metadata`));
  };

  const renderVerificationBadge = (fullPath: string) => {
    if (!isVerificationTracked(fullPath)) return null;

    const metadata = get(fieldMetadata, `${fullPath}.__metadata`);
    const currentValue = get(liveValues, fullPath);
    const hasMetadata = Boolean(metadata && typeof metadata === "object");
    const isEmailVerificationField = fullPath.startsWith("emails.");

    if (!isEmailVerificationField && !hasMetadata && !hasFilledValue(currentValue)) return null;

    const isVerified = metadata?.verified === true;
    const label = isVerified
      ? t("personalData.verification.verified")
      : t("personalData.verification.notVerified");

    return (
      <span
        className={`pd-verification-badge ${isVerified ? "is-verified" : "is-unverified"}`}
        title={label}
        aria-label={label}
      >
        {isVerified ? "✓" : "!"}
      </span>
    );
  };

  const getPatternSchema = (schemaPart: any) =>
    schemaPart?.patternProperties ? Object.values(schemaPart.patternProperties)[0] as any : null;

  const getRepeatableConfig = (fullPath: string) => {
    if (fullPath === "documents.visa") {
      return {
        keyPrefix: "visa",
        addLabel: t("personalData.actions.addVisa", { defaultValue: "Add visa" }),
        removeLabel: t("personalData.actions.removeVisa", { defaultValue: "Remove visa" }),
        itemLabel: (index: number) => t("personalData.fields.visaItem", { defaultValue: `Visa ${index + 1}`, count: index + 1 }),
        emptyLabel: t("personalData.ui.noVisas", { defaultValue: "No visas added yet" }),
      };
    }

    if (fullPath === "documents.residencePermit") {
      return {
        keyPrefix: "residence_permit",
        addLabel: t("personalData.actions.addResidencePermit", { defaultValue: "Add residence permit" }),
        removeLabel: t("personalData.actions.removeResidencePermit", { defaultValue: "Remove residence permit" }),
        itemLabel: (index: number) => t("personalData.fields.residencePermitItem", { defaultValue: `Residence permit ${index + 1}`, count: index + 1 }),
        emptyLabel: t("personalData.ui.noResidencePermits", { defaultValue: "No residence permits added yet" }),
      };
    }

    if (fullPath.startsWith("documents.technicalPassports.")) {
      return {
        keyPrefix: "technical_passport",
        addLabel: t("personalData.actions.addTechnicalPassport", { defaultValue: "Add technical passport" }),
        removeLabel: t("personalData.actions.removeTechnicalPassport", { defaultValue: "Remove technical passport" }),
        itemLabel: (index: number) => t("personalData.fields.technicalPassportItem", { defaultValue: `Technical passport ${index + 1}`, count: index + 1 }),
        emptyLabel: t("personalData.ui.noTechnicalPassports", { defaultValue: "No technical passports added yet" }),
      };
    }

    return null;
  };

  const renderRepeatablePatternSection = (schemaPart: any, fullPath: string, translatedLabel: string) => {
    const itemSchema = getPatternSchema(schemaPart);
    const config = getRepeatableConfig(fullPath);
    if (!itemSchema || !config) return null;

    const values = get(liveValues, fullPath);
    const itemKeys = values && typeof values === "object" && !Array.isArray(values)
      ? Object.keys(values).filter((key) => !isMetadataKey(key))
      : [];

    const addItem = () => {
      const nextKey = `${config.keyPrefix}_${Date.now()}`;
      setValue(`${fullPath}.${nextKey}`, {}, { shouldDirty: true, shouldValidate: true });
    };

    const removeItem = (itemKey: string) => {
      const currentItems = get(liveValues, fullPath) || {};
      const nextItems = { ...currentItems };
      delete nextItems[itemKey];
      setValue(fullPath, nextItems, { shouldDirty: true, shouldValidate: true });
    };

    return (
      <section className="pd-repeatable-section" key={fullPath}>
        <div className="pd-repeatable-section__header">
          <span className="pd-section-card__title">
            <span>{translatedLabel}</span>
          </span>
          <button type="button" className="pd-secondary-button" onClick={addItem}>
            {config.addLabel}
          </button>
        </div>

        {itemKeys.length > 0 ? (
          <div className="pd-repeatable-section__items">
            {itemKeys.map((itemKey, index) => {
              const itemPath = `${fullPath}.${itemKey}`;
              return (
                <details className="pd-section-card pd-repeatable-card" key={itemPath} open={index === 0}>
                  <summary className="pd-section-card__summary">
                    <span className="pd-section-card__title">
                      <span>{config.itemLabel(index)}</span>
                      {renderVerificationBadge(itemPath)}
                    </span>
                    <button
                      type="button"
                      className="pd-icon-button pd-repeatable-card__remove"
                      aria-label={config.removeLabel}
                      onClick={(event) => {
                        event.preventDefault();
                        removeItem(itemKey);
                      }}
                    >
                      <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                        <path d="M9 3h6l1 2h4v2H4V5h4l1-2Z" />
                        <path d="M6 9h12l-1 11H7L6 9Zm4 2v7h2v-7h-2Zm4 0v7h2v-7h-2Z" />
                      </svg>
                    </button>
                  </summary>
                  <div className="pd-section-card__body">{renderFields(itemSchema, itemPath)}</div>
                </details>
              );
            })}
          </div>
        ) : (
          <div className="pd-repeatable-section__empty">
            {config.emptyLabel}
          </div>
        )}
      </section>
    );
  };

  const renderTaxIdsSection = (fullPath: string, translatedLabel: string) => {
    const values = get(liveValues, fullPath);
    const taxItems = values && typeof values === "object" && !Array.isArray(values)
      ? Object.entries(values).filter(([key, value]) => !isMetadataKey(key) && value !== null && value !== undefined && value !== "")
      : [];

    const selectedCountry = selectedTaxCountry || countryOptions[0]?.isoCode || countryOptions[0]?.id || "";

    const addTaxId = () => {
      const countryCode = selectedCountry.toUpperCase();
      const value = taxIdentifierValue.trim();
      if (!countryCode || !value) return;
      setValue(`${fullPath}.${countryCode}`, value, { shouldDirty: true, shouldValidate: true });
      setTaxIdentifierValue("");
    };

    const removeTaxId = (countryCode: string) => {
      const currentItems = get(liveValues, fullPath) || {};
      const nextItems = { ...currentItems };
      delete nextItems[countryCode];
      setValue(fullPath, nextItems, { shouldDirty: true, shouldValidate: true });
    };

    const getCountryLabel = (countryCode: string) => {
      const country = countryOptions.find((item) =>
        item.isoCode === countryCode || item.id === countryCode || item.objectId === countryCode
      );
      return country ? `${country.flag ? `${country.flag} ` : ""}${country.name} (${countryCode})` : countryCode;
    };

    return (
      <section className="pd-repeatable-section" key={fullPath}>
        <div className="pd-repeatable-section__header pd-repeatable-section__header--stacked">
          <span className="pd-section-card__title">
            <span>{translatedLabel}</span>
          </span>
          <div className="pd-tax-id-controls">
            <SearchableSelect
              id="tax_id_country"
              name="tax_id_country"
              value={selectedCountry}
              options={countryOptions}
              placeholder={t("personalData.placeholders.selectCountry")}
              searchPlaceholder={t("personalData.placeholders.searchCountry")}
              emptyLabel={t("personalData.ui.noOptions")}
              registerField={register}
              onChange={(value) => setSelectedTaxCountry(value)}
              showOptionId={false}
            />
            <input
              className="pd-control"
              type="text"
              value={taxIdentifierValue}
              placeholder={t("personalData.placeholders.enterField", {
                field: t("personalData.fields.taxIdentifier", { defaultValue: "tax identifier" }).toLowerCase(),
              })}
              onChange={(event) => setTaxIdentifierValue(event.target.value)}
            />
            <button type="button" className="pd-secondary-button" onClick={addTaxId} disabled={!selectedCountry || !taxIdentifierValue.trim()}>
              {t("personalData.actions.addTaxId", { defaultValue: "Add tax identifier" })}
            </button>
          </div>
        </div>

        {taxItems.length > 0 ? (
          <div className="pd-repeatable-section__items">
            {taxItems.map(([countryCode, value]) => (
              <div className="pd-section-card pd-tax-id-card" key={countryCode}>
                <div className="pd-tax-id-card__content">
                  <strong>{getCountryLabel(String(countryCode))}</strong>
                  <span>{String(value)}</span>
                </div>
                <button
                  type="button"
                  className="pd-icon-button pd-repeatable-card__remove"
                  aria-label={t("personalData.actions.removeTaxId", { defaultValue: "Remove tax identifier" })}
                  onClick={() => removeTaxId(String(countryCode))}
                >
                  <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                    <path d="M9 3h6l1 2h4v2H4V5h4l1-2Z" />
                    <path d="M6 9h12l-1 11H7L6 9Zm4 2v7h2v-7h-2Zm4 0v7h2v-7h-2Z" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="pd-repeatable-section__empty">
            {t("personalData.ui.noTaxIds", { defaultValue: "No tax identifiers added yet" })}
          </div>
        )}
      </section>
    );
  };

  const renderTechnicalPassportsSection = (schemaPart: any, fullPath: string, translatedLabel: string) => {
    const itemSchema = getPatternSchema(schemaPart);
    if (!itemSchema) return null;

    const categoryOptions = itemSchema?.properties?.vehicleCategory?.enum || [];
    const selectedCategory = categoryOptions.includes(selectedTechnicalPassportCategory)
      ? selectedTechnicalPassportCategory
      : String(categoryOptions[0] || "automobile");
    const values = get(liveValues, fullPath);
    const itemKeys = values && typeof values === "object" && !Array.isArray(values)
      ? Object.keys(values).filter((key) => !isMetadataKey(key))
      : [];

    const getCategoryLabel = (categoryKey: string) => t(`personalData.fields.${categoryKey}`, {
      defaultValue: humanizeFieldName(categoryKey),
    });

    const addItem = () => {
      const nextKey = `technical_passport_${Date.now()}`;
      setValue(`${fullPath}.${nextKey}`, { vehicleCategory: selectedCategory }, { shouldDirty: true, shouldValidate: true });
    };

    const removeItem = (itemKey: string) => {
      const currentItems = get(liveValues, fullPath) || {};
      const nextItems = { ...currentItems };
      delete nextItems[itemKey];
      setValue(fullPath, nextItems, { shouldDirty: true, shouldValidate: true });
    };

    return (
      <section className="pd-repeatable-section" key={fullPath}>
        <div className="pd-repeatable-section__header pd-repeatable-section__header--stacked">
          <span className="pd-section-card__title"><span>{translatedLabel}</span></span>
          <div className="pd-repeatable-section__controls">
            <label className="pd-repeatable-section__select-label" htmlFor="technical_passport_category">
              {t("personalData.fields.vehicleCategory", { defaultValue: "Vehicle category" })}
            </label>
            <select id="technical_passport_category" className="pd-control pd-select pd-repeatable-section__select" value={selectedCategory} onChange={(event) => setSelectedTechnicalPassportCategory(event.target.value)}>
              {categoryOptions.map((categoryKey: string) => (
                <option key={categoryKey} value={categoryKey}>{getCategoryLabel(categoryKey)}</option>
              ))}
            </select>
            <button type="button" className="pd-secondary-button" onClick={addItem}>
              {t("personalData.actions.addTechnicalPassport", { defaultValue: "Add technical passport" })}
            </button>
          </div>
        </div>

        {itemKeys.length > 0 ? (
          <div className="pd-repeatable-section__items">
            {itemKeys.map((itemKey, index) => {
              const itemPath = `${fullPath}.${itemKey}`;
              const itemCategory = get(liveValues, `${itemPath}.vehicleCategory`) || selectedCategory;
              return (
                <details className="pd-section-card pd-repeatable-card" key={itemPath} open={index === 0}>
                  <summary className="pd-section-card__summary">
                    <span className="pd-section-card__title">
                      <span>{t("personalData.fields.technicalPassportItem", { defaultValue: `Technical passport ${index + 1}`, count: index + 1 })}{` · ${getCategoryLabel(String(itemCategory))}`}</span>
                      {renderVerificationBadge(itemPath)}
                    </span>
                    <button type="button" className="pd-icon-button pd-repeatable-card__remove" aria-label={t("personalData.actions.removeTechnicalPassport", { defaultValue: "Remove technical passport" })} onClick={(event) => { event.preventDefault(); removeItem(itemKey); }}>
                      <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d="M9 3h6l1 2h4v2H4V5h4l1-2Z" /><path d="M6 9h12l-1 11H7L6 9Zm4 2v7h2v-7h-2Zm4 0v7h2v-7h-2Z" /></svg>
                    </button>
                  </summary>
                  <div className="pd-section-card__body">{renderFields(itemSchema, itemPath)}</div>
                </details>
              );
            })}
          </div>
        ) : (
          <div className="pd-repeatable-section__empty">{t("personalData.ui.noTechnicalPassports", { defaultValue: "No technical passports added yet" })}</div>
        )}
      </section>
    );
  };


  const getDocumentTypeLabel = (documentType: string) =>
    t(`personalData.fields.${documentType}`, { defaultValue: humanizeFieldName(documentType) });

  const getDocumentCountryLabel = (countryCode: string) => {
    if (!countryCode) return "";
    const country = countryOptions.find((item) => item.isoCode === countryCode || item.id === countryCode || item.objectId === countryCode);
    return country ? `${country.flag ? `${country.flag} ` : ""}${country.name}` : countryCode;
  };

  const isRepeatableDocumentType = (field: any) => Boolean(field?.patternProperties);

  const getDocumentItems = () => {
    const schemaProperties = resolvedCategorySchema?.properties || {};
    const documents = liveValues?.documents && typeof liveValues.documents === "object" ? liveValues.documents : {};
    const rows: Array<{ documentType: string; key: string; path: string; value: any; schema: any; repeatable: boolean }> = [];

    Object.entries(schemaProperties).forEach(([documentType, schemaPart]: [string, any]) => {
      if (isMetadataKey(documentType)) return;
      if (documentType === "taxIds") {
        const taxIds = documents.taxIds && typeof documents.taxIds === "object" ? documents.taxIds : {};
        Object.entries(taxIds).forEach(([countryCode, value]) => {
          if (value) rows.push({ documentType, key: countryCode, path: `documents.taxIds.${countryCode}`, value: { number: value, issuingCountry: countryCode }, schema: schemaPart, repeatable: true });
        });
        return;
      }

      if (isRepeatableDocumentType(schemaPart)) {
        const values = documents[documentType] && typeof documents[documentType] === "object" ? documents[documentType] : {};
        Object.entries(values).forEach(([itemKey, value]) => {
          if (!isMetadataKey(itemKey)) rows.push({ documentType, key: itemKey, path: `documents.${documentType}.${itemKey}`, value, schema: getPatternSchema(schemaPart), repeatable: true });
        });
        return;
      }

      const value = documents[documentType];
      if (value && typeof value === "object" && Object.keys(value).some((key) => !isMetadataKey(key))) {
        rows.push({ documentType, key: documentType, path: `documents.${documentType}`, value, schema: schemaPart, repeatable: false });
      }
    });

    return rows;
  };

  const createDocument = () => {
    const schemaProperties = resolvedCategorySchema?.properties || {};
    const documentType = selectedDocumentType || Object.keys(schemaProperties).find((key) => key !== "taxIds") || "";
    if (!documentType) return;

    const schemaPart = schemaProperties[documentType];
    const countryCode = selectedDocumentCountry.toUpperCase();

    if (documentType === "taxIds") {
      setSelectedTaxCountry(countryCode || selectedTaxCountry);
      setIsAddingDocument(false);
      return;
    }

    if (isRepeatableDocumentType(schemaPart)) {
      const nextKey = `${documentType}_${Date.now()}`;
      const initialValue: Record<string, any> = {};
      if (countryCode) initialValue.issuingCountry = countryCode;
      if (documentType === "technicalPassports") initialValue.vehicleCategory = selectedTechnicalPassportCategory || "automobile";
      if (documentType === "files") initialValue.title = getDocumentTypeLabel("files");
      setValue(`documents.${documentType}.${nextKey}`, initialValue, { shouldDirty: true, shouldValidate: true });
    } else {
      const currentValue = get(liveValues, `documents.${documentType}`);
      const initialValue = currentValue && typeof currentValue === "object" ? { ...currentValue } : {};
      if (countryCode) initialValue.issuingCountry = countryCode;
      setValue(`documents.${documentType}`, initialValue, { shouldDirty: true, shouldValidate: true });
    }

    setIsAddingDocument(false);
  };

  const renderDocumentsLibrary = () => {
    const schemaProperties = resolvedCategorySchema?.properties || {};
    const documentTypes = Object.keys(schemaProperties).filter((key) => !isMetadataKey(key));
    const rows = getDocumentItems();
    const activeDocumentType = selectedDocumentType || documentTypes[0] || "";
    const activeCountry = selectedDocumentCountry || countryOptions[0]?.isoCode || countryOptions[0]?.id || "";
    const selectedSchemaPart = schemaProperties[activeDocumentType];
    const selectedPatternSchema = isRepeatableDocumentType(selectedSchemaPart) ? getPatternSchema(selectedSchemaPart) : selectedSchemaPart;
    const selectedDocumentUsesCountry = activeDocumentType !== "files" && Boolean(selectedPatternSchema?.properties?.issuingCountry || selectedPatternSchema?.properties?.country || selectedPatternSchema?.properties?.countrySpecific);

    return (
      <section className="pd-documents-library">
        <div className="pd-documents-toolbar">
          <button type="button" className="pd-primary-button" onClick={() => { setSelectedDocumentType(activeDocumentType); setSelectedDocumentCountry(activeCountry); setIsAddingDocument((value) => !value); }}>
            {t("personalData.actions.addDocument", { defaultValue: "Add document" })}
          </button>
          <div className="pd-documents-view-toggle" aria-label={t("personalData.ui.documentViewMode", { defaultValue: "View mode" })}>
            <button type="button" className={documentsViewMode === "table" ? "is-active" : ""} onClick={() => setDocumentsViewMode("table")}>{t("personalData.ui.listView", { defaultValue: "List" })}</button>
            <button type="button" className={documentsViewMode === "grid" ? "is-active" : ""} onClick={() => setDocumentsViewMode("grid")}>{t("personalData.ui.gridView", { defaultValue: "Grid" })}</button>
          </div>
        </div>

        {isAddingDocument && (
          <div className="pd-document-add-panel">
            <select className="pd-control pd-select" value={activeDocumentType} onChange={(event) => setSelectedDocumentType(event.target.value)}>
              {documentTypes.map((documentType) => <option key={documentType} value={documentType}>{getDocumentTypeLabel(documentType)}</option>)}
            </select>
            {selectedDocumentUsesCountry && (
              <SearchableSelect id="new_document_country" name="new_document_country" value={activeCountry} options={countryOptions} placeholder={t("personalData.placeholders.selectCountry")} searchPlaceholder={t("personalData.placeholders.searchCountry")} emptyLabel={t("personalData.ui.noOptions")} registerField={register} onChange={(value) => setSelectedDocumentCountry(value)} showOptionId={false} />
            )}
            <button type="button" className="pd-secondary-button" onClick={createDocument}>{t("personalData.actions.createDocument", { defaultValue: "Create" })}</button>
          </div>
        )}

        {rows.length === 0 ? (
          <div className="pd-repeatable-section__empty">{t("personalData.ui.noDocuments", { defaultValue: "No documents added yet" })}</div>
        ) : (
          <div className={documentsViewMode === "grid" ? "pd-documents-grid" : "pd-documents-table"}>
            {documentsViewMode === "table" && <div className="pd-documents-table__head"><span>{t("personalData.fields.type", { defaultValue: "Type" })}</span><span>{t("personalData.fields.issuingCountry", { defaultValue: "Country" })}</span><span>{t("personalData.fields.number", { defaultValue: "Number" })}</span><span>{t("personalData.fields.attachments", { defaultValue: "Files" })}</span></div>}
            {rows.map((row, index) => {
              const country = row.value?.issuingCountry || row.value?.country || "";
              const number = row.value?.number || row.value?.registrationNumber || row.value?.title || row.key;
              const filesCount = Array.isArray(row.value?.attachments) ? row.value.attachments.length : 0;
              return (
                <details className="pd-section-card pd-document-row" key={row.path} open={index === 0}>
                  <summary className="pd-section-card__summary pd-document-row__summary">
                    <span>{getDocumentTypeLabel(row.documentType)}</span>
                    <span>{getDocumentCountryLabel(String(country)) || "-"}</span>
                    <span>{String(number || "-")}</span>
                    <span>{filesCount}/5</span>
                    {renderVerificationBadge(row.path)}
                  </summary>
                  <div className="pd-section-card__body">{row.documentType === "taxIds" ? renderTaxIdsSection("documents.taxIds", getDocumentTypeLabel("taxIds")) : renderFields(row.schema, row.path)}</div>
                </details>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  const renderFields = (schemaPart: any, pathPrefix: string = categoryKey): JSX.Element[] => {
    if (!schemaPart?.properties) return [];

    return Object.entries(schemaPart.properties)
      .filter(([key]) => !isMetadataKey(key) && !hiddenFormFields.has(key))
      .map(([key, field]: [string, any], index) => {
      const fullPath = `${pathPrefix}.${key}`;
      const translatedLabel = t(`personalData.fields.${key}`, { defaultValue: humanizeFieldName(key) });
      const isPrimitiveField = Boolean(field?.properties?.value);
      const isLeafField = !field?.properties && !field?.patternProperties;
      const isFieldControl = isPrimitiveField || isLeafField || key === "languages";
      const isAddressSection = categoryKey === "addresses" && pathPrefix === categoryKey;

      if (fullPath === "documents.taxIds") {
        return renderTaxIdsSection(fullPath, translatedLabel) as JSX.Element;
      }

      if (fullPath === "documents.technicalPassports") {
        return renderTechnicalPassportsSection(field, fullPath, translatedLabel) as JSX.Element;
      }

      if (getRepeatableConfig(fullPath) && field?.patternProperties) {
        return renderRepeatablePatternSection(field, fullPath, translatedLabel) as JSX.Element;
      }

      if (key === "attachments" && field?.bsonType === "array") {
        const currentAttachments = (get(liveValues, fullPath) as any[]) || [];
        return (
          <AttachmentUploader
            key={fullPath}
            fieldPath={fullPath}
            label={translatedLabel}
            attachments={currentAttachments}
            onUploaded={(nextAttachments) => setValue(fullPath, nextAttachments, { shouldDirty: false })}
          />
        );
      }

      if (isFieldControl) {
        return (
          <div className="pd-field" key={fullPath}>
            <label className="pd-field__label" htmlFor={fullPath.replace(/\./g, "_")}>
              <span>{translatedLabel}</span>
              {renderVerificationBadge(fullPath)}
            </label>
            {renderFieldControl(fullPath, key)}
            {get(errors, fullPath) && (
              <InputErrorMessage msg={(get(errors, fullPath) as any)?.message || t("personalData.validation.invalidValue")} />
            )}
          </div>
        );
      }

      return (
        <details className={`pd-section-card ${isAddressSection ? "pd-address-card" : ""}`} key={fullPath} open={index === 0}>
          <summary className="pd-section-card__summary">
            <span className="pd-section-card__title">
              <span>{translatedLabel}</span>
              {renderVerificationBadge(fullPath)}
            </span>
            <small>{t("personalData.ui.expandSection", { defaultValue: "Expand section" })}</small>
          </summary>
          <div className={`pd-section-card__body ${isAddressSection ? "pd-address-card__body" : ""}`}>{renderFields(field, fullPath)}</div>
        </details>
      );
    });
  };

  const onFormSubmit: SubmitHandler<any> = async (formValues) => {
    const normalizedValues = categoryKey === "addresses"
      ? sanitizeAddressFormData(formValues)
      : formValues;
    const missingAddressFields = categoryKey === "addresses"
      ? getMissingAddressFields(formValues)
      : [];

    if (missingAddressFields.length > 0) {
      const missingLabels = missingAddressFields.map((path) => {
        const fieldName = path.split(".").pop() || path;
        return t(`personalData.fields.${fieldName}`, { defaultValue: humanizeFieldName(fieldName) });
      });
      setLocalSubmitState("idle");
      setValidationFeedback(`${t("personalData.validation.required")}: ${missingLabels.join(", ")}`);
      window.requestAnimationFrame(() => {
        const firstInvalidControl = document.getElementById(missingAddressFields[0].replace(/\./g, "_"));
        const section = firstInvalidControl?.closest("details") as HTMLDetailsElement | null;
        if (section) section.open = true;
        firstInvalidControl?.focus();
      });
      return;
    }

    try {
      setValidationFeedback("");
      setLocalSubmitState("saving");
      await onSubmit(normalizedValues);
      setLocalSubmitState("success");
    } catch (error) {
      setLocalSubmitState("idle");
      throw error;
    }
  };

  const onFormInvalid = () => {
    setLocalSubmitState("idle");
    setValidationFeedback(t("personalData.validation.invalidValue"));
  };

  if (!zodSchema || !categorySchema) {
    return (
      <div className="pd-shimmer-stack" aria-hidden="true">
        <div className="pd-shimmer pd-shimmer--hero" />
        <div className="pd-shimmer pd-shimmer--line" />
        <div className="pd-shimmer pd-shimmer--line short" />
      </div>
    );
  }

  const submitFeedbackText =
    submitState === "saved"
      ? t("personalData.ui.savedState", { defaultValue: "Server state updated" })
      : submitState === "saving"
        ? t("personalData.ui.savingState", { defaultValue: "Sending changes..." })
        : submitState === "error"
          ? t("personalData.ui.errorState", { defaultValue: "Retry recommended" })
          : submitState === "draft"
            ? t("personalData.ui.draftState", { defaultValue: "Changes saved to draft" })
            : "";

  return (
    <form className="pd-form-engine" noValidate onSubmit={handleSubmit(onFormSubmit, onFormInvalid)}>
      {isEmptyState && (
        <div className="pd-empty-state">
          <strong>{t("personalData.ui.emptyStateTitle", { defaultValue: "Start shaping this section" })}</strong>
        </div>
      )}

      <div className="pd-form-grid">{categoryKey === "documents" ? renderDocumentsLibrary() : renderFields(resolvedCategorySchema)}</div>

      <footer className="pd-form-footer">
        {validationFeedback && (
          <div className="pd-submit-feedback pd-submit-feedback--error" role="alert">
            {validationFeedback}
          </div>
        )}
        {submitFeedbackText && (
          <div className={`pd-submit-feedback pd-submit-feedback--${submitState}`}>
            {submitFeedbackText}
          </div>
        )}

        <button
          type="submit"
          className={`pd-primary-button ${localSubmitState === "saving" ? "is-loading" : ""} ${localSubmitState === "success" ? "is-success" : ""}`}
          disabled={localSubmitState === "saving"}
        >
          <span className="pd-button__label">
            {localSubmitState === "success"
              ? t("personalData.ui.savedCta", { defaultValue: "Saved" })
              : localSubmitState === "saving"
                ? t("personalData.ui.savingCta", { defaultValue: "Saving..." })
                : t("personalData.actions.saveChanges")}
          </span>
        </button>
      </footer>
    </form>
  );
};

export default DynamicFormWithNestedTabs;
